import type { IUiSoundService } from "@runtime/audio/UiSoundService";
import type { AIInterfaceModel } from "./AIInterfaceModel";

/**
 * `aiSoundCues` — the sounds that mark Spark interactions.
 *
 * **Web-only.** Unity's Spark plays no sounds. The cues and their rule come
 * from Alex's design kit (alexdevkit v2, `kit/sound`; user direction,
 * 2026-10-28): a sound marks something the user did or something that arrived.
 * Push-to-talk's two use the OpenHome prototype's mute glides instead (user
 * direction the same day: the kit-style ones were not liked there).
 *
 * | Moment | Event | Cue |
 * | --- | --- | --- |
 * | Voice mode entered / left | `onVoiceModeChanged` | `enter` / `leave` |
 * | Mic unmuted / muted in voice mode | `onMicrophoneToggled` | `on` / `off` |
 * | Push-to-talk opened (Space, a prompt tap, or Spark's `Listen_User`) | `onPushToTalkActiveChanged(true)` | `listen` |
 * | Push-to-talk heard no speech and closed | `onListeningTimedOut` | `expire` |
 * | A typed message, image or file sent | `onUserMessageSent` | `send` |
 * | A reply that arrives as text only | `onAssistantTextCompleted` | `arrive` |
 *
 * Three silences are deliberate:
 * - **A spoken reply gets no `arrive`.** Spark's voice already announces it. In
 *   practice the cue plays for a text-bridged role with voice off
 *   (`workflow-builder` ships so).
 * - **A push-to-talk window that heard the user closes silently.** Spark's
 *   answer follows it. Only the timeout, after which nothing answers, plays.
 * - **Entering and leaving voice mode play no `on` / `off`**, although each
 *   opens or closes the mic. That is why the mic cues listen to the two
 *   gestures' own events and not to `onMutedChanged`, which also fires for
 *   those, for `Use_ConstantListening` and for a mic that failed to open.
 *
 * It subscribes to the interface model in the composition root, so exactly one
 * instance plays each cue. `useSpark()` runs once per mounted `AiPanel`, and a
 * cue played from there would play twice, or not at all with no panel mounted.
 */
export function connectAISoundCues(
  interfaceModel: AIInterfaceModel,
  sound: IUiSoundService,
): () => void {
  const offs = [
    interfaceModel.onVoiceModeChanged.on((entered) => sound.play(entered ? "enter" : "leave")),
    interfaceModel.onMicrophoneToggled.on((open) => sound.play(open ? "on" : "off")),
    interfaceModel.onPushToTalkActiveChanged.on((open) => {
      if (open) sound.play("listen");
    }),
    interfaceModel.onListeningTimedOut.on(() => sound.play("expire")),
    interfaceModel.onUserMessageSent.on(() => sound.play("send")),
    interfaceModel.onAssistantTextCompleted.on(({ spoken }) => {
      if (!spoken) sound.play("arrive");
    }),
  ];
  return () => offs.forEach((off) => off());
}
